// Enemy steering (all archetypes per their def), separation, contact damage,
// and death handling. Conforms to GDD §7.2.1, §7.3, §7.4 and §4.10; archetype
// variety (weaver sway, per-def bulk/speed) per spec-arsenal. Swarm texture:
// per-enemy speed stagger, archetype gaits (lunge/surge/shove/weave jitter),
// and distance-based pursuit pressure keep the horde from moving as one blob
// or trailing the player in a loose cloud — all deterministic from stable
// per-enemy values (pool_index, spawn_tick, age_ticks), never from wall clock
// or Math.random.

import type { WorldState } from './state.js';
import type { Enemy, Pickup, Projectile, Vec2, EnemyDef } from '../types.js';
import { cfg } from '../config.js';
import { SpatialHash } from './spatial-hash.js';
import { wrapArena, wrappedDelta, staticCircleOverlap, resolveStaticSeparation } from './collision.js';
import { enemyDamageAtTime, applyDamageToPlayer, applyRamDamageToPlayer, ramDamageForSpeed, RAM_SPEED_THRESHOLD, updateShieldRegen } from './combat.js';
import { updateTurret } from './turret.js';
import { getSerenityMods } from './serenity.js';
import { updateBossAI } from './boss-ai.js';
import { initEnemy } from './director.js';
import { foldIntoOldestMote } from './kill.js';
import { spawnEnemyBullet } from './weapon-system.js';
import { RngService } from './rng.js';
import enemiesJson from '../content/enemies.json';

const ENEMY_DEFS = (enemiesJson as { version: string; entries: EnemyDef[] }).entries;
const ENEMY_DEF_MAP = new Map<string, EnemyDef>(ENEMY_DEFS.map((d) => [d.id, d]));

const SEPARATION_RADIUS = 3;
const NEIGHBOR_RADIUS = 6;
const JITTER_STRENGTH = 0.05;
const SEPARATION_ITERATIONS = 1;
// Shield bash: when the energy shield absorbs a contact hit, the attacker is
// shoved this many units away instantly and flung at this speed (u/s); the
// next tick's steering re-approaches from there, so the bounce reads.
const SHIELD_BASH_SHOVE = 2.5;
const SHIELD_BASH_SPEED = 12;
// Player-local repel: inside this radius enemies feel an outward push ramping
// to REPEL_STRENGTH × the pursuit weight, keeping a clearing around the ship
// at contact range (the "amoeba" collapse from the art review).
const REPEL_RADIUS = 3.5;
const REPEL_STRENGTH = 1.2;
// Pursuit pressure: beyond CLOSING_NEAR units from the player the speed cap
// ramps up by up to CLOSING_MAX_BOOST at CLOSING_NEAR + CLOSING_SPAN, so the
// horde actually closes on a kiting player instead of trailing in a loose
// cloud. Inside CLOSING_NEAR there is no boost, preserving orbit/standoff
// balance at contact range.
const CLOSING_NEAR = 8;
const CLOSING_SPAN = 22;
const CLOSING_MAX_BOOST = 0.5;
// Per-enemy speed stagger band (× def.max_speed): desyncs arrivals, pursuits,
// and standoff distances so the swarm reads as individuals, not one blob.
const STAGGER_MIN = 0.85;
const STAGGER_MAX = 1.25;
// Brood carriers (EnemyDef.brood): produced minis get the standard spawn
// grace, pop in at this radial offset from the carrier's hull, and the
// carrier anchors ~BROOD_STANDOFF units off the player (producer, not ramer).
const BROOD_GRACE_TICKS = 12;
const BROOD_SPAWN_OFFSET = 0.8;
const BROOD_STANDOFF = 14;
// Children-claim maintenance cadence per carrier (phased by pool_index so a
// brood of carriers doesn't prune in lockstep).
const BROOD_MAINTAIN_TICKS = 15;
// Leech latch (EnemyDef.latch): a dash start flings every latched leech off
// radially at this speed (u/s); any detach (expiry or shake-off) starts this
// re-latch cooldown (ticks).
const LEECH_SHAKEOFF_SPEED = 14;
const LATCH_RELATCH_COOLDOWN_TICKS = 60;
// Largest def hitbox: broadphase queries must reach any possible pair partner.
const MAX_HITBOX_RADIUS = Math.max(...ENEMY_DEFS.map((d) => d.hitbox_radius));

const spatialHashCache = new WeakMap<WorldState, SpatialHash>();

// --- Swarm texture (deterministic, no RNG-stream consumption) -------------
// Every per-enemy variation derives from a pure hash of stable sim values
// (pool_index, spawn_tick, age_ticks), so replays and same-seed runs are
// bit-identical and no stream draws are reordered.

function hashU32(a: number, b: number): number {
  // Salted inputs so the (0, 0) pair — the first pool slot at spawn tick 0,
  // common in tests — doesn't degenerate to a fixed point.
  let h = (Math.imul(a ^ 0xA53A9DB3, 0x9E3779B1) ^ Math.imul(b ^ 0xC3C9E5A5, 0x85EBCA6B)) >>> 0;
  h = Math.imul(h ^ (h >>> 15), 0x2C1B3C6D);
  h = Math.imul(h ^ (h >>> 12), 0x297A2D39);
  h ^= h >>> 15;
  return h >>> 0;
}

function hashUnit(a: number, b: number): number {
  return hashU32(a, b) / 4294967296;
}

/** Per-enemy speed stagger in [STAGGER_MIN, STAGGER_MAX]. Also scales the
 * enemy's standoff distances (orbit radius, spitter standoff) so the
 * "donut" ring thickens instead of holding one uniform radius. */
export function speedStaggerFactor(poolIndex: number, spawnTick: number): number {
  return STAGGER_MIN + (STAGGER_MAX - STAGGER_MIN) * hashUnit(poolIndex, spawnTick);
}

/** Lunge-pause gait multiplier (EnemyDef.lunge): bursts at burst_mult, then
 * creeps at pause_mult; the cycle phase is offset per enemy via the pool
 * index hash so a squad of dartlings never lunges in lockstep. */
export function lungeSpeedMult(
  ageTicks: number,
  poolIndex: number,
  lunge: { burst_mult: number; burst_ticks: number; pause_mult: number; pause_ticks: number }
): number {
  const cycle = lunge.burst_ticks + lunge.pause_ticks;
  const phase = hashU32(poolIndex, 0x1CE5B00B) % cycle;
  return (ageTicks + phase) % cycle < lunge.burst_ticks ? lunge.burst_mult : lunge.pause_mult;
}

/** Surging sub-flock multiplier (EnemyDef.surge): flock = pool_index %
 * flocks; each flock's speed breathes on a quarter-period phase offset so
 * the horde pulses in `flocks` offset waves instead of moving as one blob. */
export function surgeSpeedMult(
  ageTicks: number,
  poolIndex: number,
  surge: { flocks: number; amplitude: number; period_ticks: number }
): number {
  const flock = poolIndex % surge.flocks;
  const phase = (2 * Math.PI * ageTicks) / surge.period_ticks + (flock * Math.PI) / 2;
  return 1 + surge.amplitude * Math.sin(phase);
}

/** Combined per-enemy gait multiplier for this tick (1 for defs without
 * gaits). Pure in (def, pool_index, age_ticks). */
function gaitSpeedMult(e: Enemy, def: EnemyDef): number {
  let mult = 1;
  if (def.lunge) mult *= lungeSpeedMult(e.age_ticks, e.pool_index, def.lunge);
  if (def.surge) mult *= surgeSpeedMult(e.age_ticks, e.pool_index, def.surge);
  return mult;
}

/** Per-carrier brood production phase in [0, period_ticks): a pure hash of
 * the pool index, so two carriers sharing a period almost never share a
 * production tick (no RNG-stream consumption). */
export function broodPhaseOffset(poolIndex: number, periodTicks: number): number {
  return hashU32(poolIndex, 0xB400D5) % periodTicks;
}

/** True while `e` holds a live latch this life. The bookkeeping must postdate
 * this life's spawn and not lie in the future: pool slots are reused and
 * deaths resolved outside enemy-ai (weapon kills via kill.ts) don't clear
 * these additive fields, and a run restart resets the tick counter. */
function isLatched(e: Enemy, tick: number): boolean {
  return (
    e.latch_until !== undefined &&
    e.latch_until > tick &&
    e.latch_tick !== undefined &&
    e.latch_tick >= e.spawn_tick &&
    e.latch_tick <= tick
  );
}

// Per-archetype def lookup (spec-arsenal): speed, force, turn rate, contact
// damage, and the weave trait all come from the enemy's own def now.
function getEnemyDef(id: string): EnemyDef {
  const def = ENEMY_DEF_MAP.get(id);
  if (!def) {
    throw new Error(`Enemy definition ${id} not found in enemies.json`);
  }
  return def;
}

function getSpatialHash(world: WorldState): SpatialHash {
  let sh = spatialHashCache.get(world);
  if (!sh) {
    sh = new SpatialHash(world.enemies.capacity);
    spatialHashCache.set(world, sh);
  }
  return sh;
}

function vec2(x = 0, y = 0): Vec2 {
  return { x, y };
}

// Fast shortest signed delta across the seam for inputs already within one
// arena of each other. Branch-based; avoids function-call and modulo cost in
// the neighbor loop.
function fastDelta(a: Vec2, b: Vec2, out: Vec2): Vec2 {
  let dx = a.x - b.x;
  let dy = a.y - b.y;
  if (dx > 100) dx -= 200; else if (dx < -100) dx += 200;
  if (dy > 100) dy -= 200; else if (dy < -100) dy += 200;
  out.x = dx;
  out.y = dy;
  return out;
}

function add(a: Vec2, b: Vec2): Vec2 {
  return { x: a.x + b.x, y: a.y + b.y };
}

function sub(a: Vec2, b: Vec2): Vec2 {
  return { x: a.x - b.x, y: a.y - b.y };
}

function scale(a: Vec2, s: number): Vec2 {
  return { x: a.x * s, y: a.y * s };
}

function len(a: Vec2): number {
  return Math.sqrt(a.x * a.x + a.y * a.y);
}

function normalize(a: Vec2): Vec2 {
  const l = len(a);
  if (l < 1e-8) return { x: 0, y: 0 };
  return { x: a.x / l, y: a.y / l };
}

function clampMagnitude(a: Vec2, max: number): Vec2 {
  const l = len(a);
  if (l <= max) return a;
  const s = max / l;
  return { x: a.x * s, y: a.y * s };
}

function limitTurn(vel: Vec2, oldAngle: number, turnRateRadPerSec: number, dt: number): void {
  const speed = len(vel);
  if (speed < 1e-4) return;
  const newAngle = Math.atan2(vel.y, vel.x);
  let delta = newAngle - oldAngle;
  while (delta > Math.PI) delta -= 2 * Math.PI;
  while (delta < -Math.PI) delta += 2 * Math.PI;
  const maxDelta = turnRateRadPerSec * dt;
  if (Math.abs(delta) <= maxDelta) return;
  const clampedAngle = oldAngle + Math.sign(delta) * maxDelta;
  vel.x = Math.cos(clampedAngle) * speed;
  vel.y = Math.sin(clampedAngle) * speed;
}

function buildEnemyMap(world: WorldState): Map<number, Enemy> {
  const map = new Map<number, Enemy>();
  for (const e of world.enemies.alive()) {
    map.set(e.id, e);
  }
  return map;
}

function computeSteering(
  e: Enemy,
  world: WorldState,
  sh: SpatialHash,
  byId: Map<number, Enemy>,
  rng: RngService
): Vec2 {
  const weights = e.steering.weights;
  if (weights.length < 6) {
    throw new Error(`Enemy ${e.id} steering weights incomplete`);
  }

  let separation = vec2();
  let alignmentSum = vec2();
  let centroid = vec2();
  let neighborCount = 0;
  let separationCount = 0;
  const scratchDelta = vec2();

  const handleNeighbor = (id: number): void => {
    const n = byId.get(id);
    if (!n || n.id === e.id) return;
    // Only same-plane neighbors count for flocking/separation; the virtual
    // depth layers let up to 3 enemies share the same (x, y) space.
    if (n.plane !== e.plane) return;
    // Shortest signed delta across the seam (toroidal neighborhood).
    const d = fastDelta(e.pos, n.pos, scratchDelta);
    const distSq = d.x * d.x + d.y * d.y;
    if (distSq < 1e-8) return;
    const dist = Math.sqrt(distSq);

    if (dist < SEPARATION_RADIUS) {
      // Push away, weighted inversely by distance.
      const push = 1 / dist;
      separation.x += (d.x / dist) * push;
      separation.y += (d.y / dist) * push;
      separationCount++;
    }

    if (dist < NEIGHBOR_RADIUS) {
      alignmentSum.x += n.vel.x;
      alignmentSum.y += n.vel.y;
      // Accumulate the neighbor position unwrapped into e's local frame so
      // the centroid stays meaningful for flocks straddling the seam.
      centroid.x += e.pos.x + d.x;
      centroid.y += e.pos.y + d.y;
      neighborCount++;
    }
  };

  sh.forEachInCircle(e.pos, NEIGHBOR_RADIUS, (id) => byId.get(id)?.pos, handleNeighbor, e.id);

  // When the current plane is crowded, drift to an adjacent plane with more
  // room. Deterministic and throttled so planes don't flicker.
  if (separationCount > 6 && (world.tick + e.pool_index) % 45 === 0) {
    const options = ([0, 1, 2] as const).filter((p) => p !== e.plane);
    e.plane = options[(e.pool_index + world.tick) % options.length] as 0 | 1 | 2;
  }

  const separationSteer = separationCount > 0 ? normalize(separation) : vec2();
  const alignmentSteer =
    neighborCount > 0 ? normalize(scale(alignmentSum, 1 / neighborCount)) : vec2();
  const cohesionSteer =
    neighborCount > 0
      ? normalize(sub(scale(centroid, 1 / neighborCount), e.pos))
      : vec2();

  // Per-enemy stagger (bosses exempt: boss-ai.ts owns their pacing). Scales
  // standoff distances here and the speed cap in updateEnemies.
  const stagger = e.boss ? 1 : speedStaggerFactor(e.pool_index, e.spawn_tick);

  // Pursuit: basic seek toward player (shortest path across the seam),
  // upgraded per archetype by its hunting technique (EnemyDef.hunt).
  const toPlayer = fastDelta(world.player.pos, e.pos, vec2());
  let pursuitSteer = normalize(toPlayer);
  const distToPlayer = len(toPlayer);
  const hunt = getEnemyDef(e.def_id).hunt;
  if (hunt) {
    const dist = distToPlayer;
    const side = e.pool_index % 2 === 0 ? 1 : -1;
    if (hunt.kind === 'intercept' && dist > 1e-6) {
      // Aim where the player will be when we arrive, not where they are now.
      const lead = Math.min(hunt.value, dist / getEnemyDef(e.def_id).max_speed);
      const target = {
        x: world.player.pos.x + world.player.vel.x * lead,
        y: world.player.pos.y + world.player.vel.y * lead,
      };
      wrapArena(target);
      pursuitSteer = normalize(fastDelta(target, e.pos, vec2()));
    } else if (hunt.kind === 'flank' && dist > 1e-6) {
      // Approach on an arc: aim beside the player, tightening to a direct
      // pursuit as we close in (perpendicular side alternates per enemy).
      const radius = hunt.value;
      const arc = Math.max(0, Math.min(1, (dist - radius * 0.5) / radius));
      const target = {
        x: world.player.pos.x + (-toPlayer.y / dist) * side * radius * arc,
        y: world.player.pos.y + (toPlayer.x / dist) * side * radius * arc,
      };
      wrapArena(target);
      pursuitSteer = normalize(fastDelta(target, e.pos, vec2()));
    } else if (hunt.kind === 'orbit' && dist > 1e-6 && dist < hunt.value * stagger) {
      // Pack orbit: inside the (per-enemy staggered) radius, circle the player
      // tangentially with a slight inward pull instead of stacking head-on.
      const tx = (-toPlayer.y / dist) * side;
      const ty = (toPlayer.x / dist) * side;
      pursuitSteer = normalize({
        x: tx + (-toPlayer.x / dist) * 0.35,
        y: ty + (-toPlayer.y / dist) * 0.35,
      });
    }
  }

  // Spitter standoff (EnemyDef.spit): inside ~80% of its (per-enemy
  // staggered) standoff range the pursuit inverts into a direct retreat so the
  // lobber re-opens its firing lane; this overrides the flank arc, which would
  // otherwise keep closing.
  const spit = getEnemyDef(e.def_id).spit;
  if (spit && distToPlayer > 1e-6 && distToPlayer < spit.standoff * stagger * 0.8) {
    pursuitSteer = normalize({ x: -toPlayer.x, y: -toPlayer.y });
  }

  // Brood carrier anchor (EnemyDef.brood): producers hold a slow standoff
  // ring ~BROOD_STANDOFF off the player instead of closing to ram — inside
  // 80% of the (per-enemy staggered) ring the pursuit inverts into a direct
  // retreat, mirroring the spitter rule.
  const brood = getEnemyDef(e.def_id).brood;
  if (brood && distToPlayer > 1e-6 && distToPlayer < BROOD_STANDOFF * stagger * 0.8) {
    pursuitSteer = normalize({ x: -toPlayer.x, y: -toPlayer.y });
  }

  // The torus has no walls, so the obstacle-avoidance weight acts on a zero
  // vector; the slot is kept so content steering_weights indices stay valid.
  const obstacleSteer = vec2();

  // Formation goal: seek the director-assigned goal point. The goal is fixed
  // at spawn time, so once the enemy arrives or overshoots, the goal sits
  // behind the pursuit line and its pull cancels the pursuit — the swarm
  // loiters around the stale point (the "loose cloud holding behind" from the
  // art review). Release the goal with its alignment to the live pursuit:
  // full weight while it pulls toward the player, fading to none once it
  // falls behind. (Bosses exempt: their goal semantics belong to boss-ai.ts.)
  const toGoal = fastDelta(e.steering.goal, e.pos, vec2());
  const goalSteer = normalize(toGoal);
  const goalAlignment = goalSteer.x * pursuitSteer.x + goalSteer.y * pursuitSteer.y;
  const goalFade = e.boss ? 1 : Math.max(0, goalAlignment);

  // Shove-through gait (EnemyDef.shove): tanks grind through the crowd —
  // their separation weight is scaled down so pursuit/goal dominate instead.
  const separationWeight = weights[0] * (getEnemyDef(e.def_id).shove?.separation_mult ?? 1);

  // Deterministic steering jitter from the `ai` stream.
  const ai = rng.get('ai');
  const jitterX = ai.next_range(-JITTER_STRENGTH, JITTER_STRENGTH);
  const jitterY = ai.next_range(-JITTER_STRENGTH, JITTER_STRENGTH);

  const desired = vec2(
    separationWeight * separationSteer.x +
      weights[1] * alignmentSteer.x +
      weights[2] * cohesionSteer.x +
      weights[3] * pursuitSteer.x +
      weights[4] * obstacleSteer.x +
      weights[5] * goalFade * goalSteer.x +
      jitterX,
    separationWeight * separationSteer.y +
      weights[1] * alignmentSteer.y +
      weights[2] * cohesionSteer.y +
      weights[3] * pursuitSteer.y +
      weights[4] * obstacleSteer.y +
      weights[5] * goalFade * goalSteer.y +
      jitterY
  );

  // Player-local repel (bosses exempt): inside REPEL_RADIUS of the ship the
  // outward push ramps up as the distance closes, so the pack thins into a
  // readable clearing around the halo instead of collapsing into one amoeba
  // on top of the player. Soft enough that attackers still get through —
  // it delays, never walls off.
  if (!e.boss && distToPlayer > 1e-6 && distToPlayer < REPEL_RADIUS) {
    const k = (1 - distToPlayer / REPEL_RADIUS) * REPEL_STRENGTH;
    desired.x -= (toPlayer.x / distToPlayer) * k;
    desired.y -= (toPlayer.y / distToPlayer) * k;
  }

  // Weaver sway (spec-arsenal): a perpendicular sine component driven by the
  // def's weave trait, phased per enemy so a squad doesn't sway in lockstep.
  // weave.jitter additionally jitters amplitude (±jitter) and period
  // (±jitter/2) per enemy. Deterministic from sim state only (age ticks +
  // pool index hash, no RNG).
  const weave = getEnemyDef(e.def_id).weave;
  if (weave) {
    const j = weave.jitter ?? 0;
    const amplitude = weave.amplitude * (1 + j * (2 * hashUnit(e.pool_index, 0xEA1E) - 1));
    const period =
      weave.period_ticks * (1 + j * 0.5 * (2 * hashUnit(e.pool_index, 0x9E11) - 1));
    const phase = (2 * Math.PI * e.age_ticks) / period + e.pool_index;
    const sway = amplitude * Math.sin(phase);
    desired.x += -pursuitSteer.y * sway;
    desired.y += pursuitSteer.x * sway;
  }

  // Dodger bullet-evade (EnemyDef.evade): the nearest ally bullet inside the
  // evade radius adds a strong perpendicular sidestep away from its velocity
  // line; the side alternates per pool index so a squad doesn't all dodge the
  // same way. Deterministic: nearest-first scan in pool order, no RNG.
  const evade = getEnemyDef(e.def_id).evade;
  if (evade) {
    let nearest: Projectile | null = null;
    let nearestSq = evade.radius * evade.radius;
    for (const p of world.projectiles.alive()) {
      if (p.faction !== 'ally' || !p.alive) continue;
      const d = fastDelta(p.pos, e.pos, scratchDelta);
      const distSq = d.x * d.x + d.y * d.y;
      if (distSq >= nearestSq) continue;
      nearest = p;
      nearestSq = distSq;
    }
    if (nearest) {
      const bulletSpeed = len(nearest.vel);
      if (bulletSpeed > 1e-6) {
        const side = e.pool_index % 2 === 0 ? 1 : -1;
        const nx = nearest.vel.x / bulletSpeed;
        const ny = nearest.vel.y / bulletSpeed;
        desired.x += -ny * side * evade.strength;
        desired.y += nx * side * evade.strength;
      }
    }
  }

  return desired;
}

function spawnXpMote(world: WorldState, pos: Vec2, value: number, tick: number): void {
  const p = world.pickups.acquire();
  if (!p) {
    // Pool full: fold into the oldest live mote (same rule as kill.ts).
    foldIntoOldestMote(world, value);
    return;
  }
  p.pickup_kind = 'xp_mote';
  p.value = value;
  p.pos = { x: pos.x, y: pos.y };
  p.vel = { x: 0, y: 0 };
  p.hp = 1;
  p.max_hp = 1;
  p.alive = true;
  p.spawn_tick = tick;
  p.targetable = false;
  p.magnet = true;
  world.events.emit('spawn', { kind: 'pickup', pickup_kind: 'xp_mote', pos: p.pos, value }, tick);
}

/**
 * Update all living driftfin enemies: steering, separation, movement,
 * contact damage, and death/loot resolution.
 */
export function updateEnemies(world: WorldState, dt: number, tick: number): void {
  if (dt <= 0) return;

  // Player-owned per-tick systems hooked here so game.ts keeps a single sim
  // call list: shield regen (combat.ts) and the hold-ground turret
  // (turret.ts). Both are tick-deterministic.
  updateShieldRegen(world, tick, getSerenityMods(world).shieldRegenMult);
  updateTurret(world, tick);

  const rng = world.rng;
  const events = world.events;
  const sh = getSpatialHash(world);
  const stepRate = cfg.getNumber('sim.step_rate_hz');
  const mercyTicks = Math.ceil(cfg.getNumber('player.mercy_window') * stepRate);
  const contactKnockback = cfg.getNumber('player.contact_knockback');
  const player = world.player;

  // Rebuild spatial hash deterministically in pool-index order.
  sh.clear();
  const byId = buildEnemyMap(world);
  for (const e of world.enemies.alive()) {
    sh.insert(e);
  }

  // Brood carriers (EnemyDef.brood): every period_ticks of age — phase-offset
  // per carrier via pool_index so a brood never pulses in lockstep — produce
  // one mini at the carrier's position with the standard spawn grace, while
  // its living children stay under max_children and the pool has room.
  // Children are claimed by id in brood_child_ids (reciprocated by their
  // mother_id): kill.ts releases dead children without touching these fields,
  // but a released/reused slot fails the id lookup, so claims prune
  // themselves deterministically. Bosses exempt. No RNG-stream consumption.
  for (const e of world.enemies.aliveArray()) {
    const brood = getEnemyDef(e.def_id).brood;
    if (!brood || e.boss) continue;
    if (tick < e.telegraph_until) continue;

    const due =
      (e.age_ticks + broodPhaseOffset(e.pool_index, brood.period_ticks)) %
        brood.period_ticks ===
      0;
    // Refresh the children cache on production ticks and on a throttled
    // 15-tick cadence (render reads brood_children for the brood-sac glow).
    if (due || (tick + e.pool_index) % BROOD_MAINTAIN_TICKS === 0) {
      let kept: number[] | undefined;
      if (e.brood_child_ids) {
        for (const id of e.brood_child_ids) {
          const c = byId.get(id);
          if (c && c.mother_id === e.id) (kept ??= []).push(id);
        }
      }
      e.brood_child_ids = kept;
      e.brood_children = kept?.length ?? 0;
    }
    if (!due) continue;
    if ((e.brood_child_ids?.length ?? 0) >= brood.max_children) continue;

    const child = world.enemies.acquire();
    if (!child) {
      events.emit('pool_exhausted', { kind: 'enemy' }, tick);
      continue;
    }
    const childDef = getEnemyDef(brood.def_id);
    const angle = (hashU32(e.pool_index, e.age_ticks) % 360) * (Math.PI / 180);
    const pos = {
      x: e.pos.x + Math.cos(angle) * BROOD_SPAWN_OFFSET,
      y: e.pos.y + Math.sin(angle) * BROOD_SPAWN_OFFSET
    };
    wrapArena(pos);
    const telegraphUntil = tick + BROOD_GRACE_TICKS;
    initEnemy(child, childDef, pos, world.player.pos, world.runTime, tick, telegraphUntil);
    child.mother_id = e.id;
    (e.brood_child_ids ??= []).push(child.id);
    e.brood_children = e.brood_child_ids.length;
    events.emit(
      'spawn',
      {
        kind: 'enemy',
        def_id: childDef.id,
        pos: { x: pos.x, y: pos.y },
        telegraph_until: telegraphUntil,
        mother_id: e.id
      },
      tick
    );
  }

  // Steering + velocity integration.
  for (const e of world.enemies.alive()) {
    // Enemies materialize after their telegraph; until then they are frozen.
    if (tick < e.telegraph_until) {
      e.vel.x = 0;
      e.vel.y = 0;
      continue;
    }
    if (!e.targetable) {
      e.targetable = true;
      // Fresh life on this pool slot: drop latch/brood bookkeeping left by a
      // previous tenant — deaths resolved outside enemy-ai (weapon kills via
      // kill.ts) release slots without clearing these additive fields.
      // mother_id is NOT cleared here: a brood-produced mini materializes 12
      // ticks after production set the link, and stale links are harmless —
      // the carrier's claim prune checks id + reciprocal mother_id, so a
      // reused slot can never ghost a child.
      e.latch_until = undefined;
      e.latch_tick = undefined;
      e.latch_drain_acc = undefined;
      e.latch_detached_tick = undefined;
      e.brood_child_ids = undefined;
      e.brood_children = undefined;
      // Subspace formation drive from a previous tenant of this pool slot.
      e.formation_drive = undefined;
      events.emit('materialize', { entity_id: e.id, pos: e.pos }, tick);
    }

    // Latched leech: no steering — the contact phase pins it to the player's
    // hull and drains HP there.
    if (isLatched(e, tick)) {
      e.vel.x = 0;
      e.vel.y = 0;
      continue;
    }

    const def = getEnemyDef(e.def_id);
    const turnRateRad = (def.turn_rate * Math.PI) / 180;
    const oldVel = { x: e.vel.x, y: e.vel.y };

    // Boss state machines (boss-ai.ts): attack phases override the default
    // pursuit and can raise the speed cap (razor lunge); bullet volleys fire
    // as side effects inside the hook. Null = default pursuit this tick.
    // Bosses are exempt from every swarm-texture modifier below: their pacing
    // belongs to boss-ai.ts alone.
    const bossCtl = e.boss ? updateBossAI(e, world, tick) : null;
    // Subspace formation flight (sim/subspace.ts): the formation controller's
    // per-tick drive overrides the def's pursuit blend through the same
    // channel, and is likewise exempt from the swarm-texture modifiers (a
    // staggered formation is no formation at all).
    const drive = e.boss ? null : (e.formation_drive ?? null);
    const desired = bossCtl ? bossCtl.desired : drive ? drive : computeSteering(e, world, sh, byId, rng);
    let maxSpeed = def.max_speed * (bossCtl?.speedMult ?? 1) * (drive?.speed_mult ?? 1);
    if (!e.boss && !drive) {
      // Swarm texture: per-enemy speed stagger × archetype gait × pursuit
      // pressure. The pressure term ramps the cap up with distance (0 inside
      // CLOSING_NEAR) so base pursuit actually closes on a kiting player
      // instead of trailing in a loose cloud.
      const dist = len(fastDelta(player.pos, e.pos, vec2()));
      const closing =
        1 + Math.min(Math.max((dist - CLOSING_NEAR) / CLOSING_SPAN, 0), 1) * CLOSING_MAX_BOOST;
      maxSpeed *= speedStaggerFactor(e.pool_index, e.spawn_tick) * gaitSpeedMult(e, def) * closing;
    }

    // Ranged lobbers (EnemyDef.spit): one slow aimed bullet per period, at
    // the player's current position.
    const spit = def.spit;
    if (spit && player.alive && e.age_ticks > 0 && e.age_ticks % spit.period_ticks === 0) {
      const dir = normalize(fastDelta(player.pos, e.pos, vec2()));
      if (dir.x !== 0 || dir.y !== 0) {
        spawnEnemyBullet(world, e, def, dir, spit.bullet_speed, tick);
      }
    }

    // desired = Σ wi·Si; accel = clamp(desired·max_speed − v, |·| ≤ max_force)
    const targetVel = scale(desired, maxSpeed);
    const accel = clampMagnitude(sub(targetVel, e.vel), def.max_force);

    e.vel.x += accel.x * dt;
    e.vel.y += accel.y * dt;

    // Clamp speed (boss attack phases can raise the cap via maxSpeed).
    const speed = len(e.vel);
    if (speed > maxSpeed) {
      const s = maxSpeed / speed;
      e.vel.x *= s;
      e.vel.y *= s;
    }

    // Clamp turn rate.
    const oldAngle = len(oldVel) > 1e-4 ? Math.atan2(oldVel.y, oldVel.x) : Math.atan2(desired.y, desired.x);
    limitTurn(e.vel, oldAngle, turnRateRad, dt);
  }

  // Position integration + toroidal wrap.
  for (const e of world.enemies.alive()) {
    if (tick < e.telegraph_until) continue;
    e.pos.x += e.vel.x * dt;
    e.pos.y += e.vel.y * dt;
    wrapArena(e.pos);
    e.age_ticks++;
  }

  // Enforce minimum center separation (GDD §7.4: ≥ 0.7×(r1+r2)).
  for (let iter = 0; iter < SEPARATION_ITERATIONS; iter++) {
    for (const e of world.enemies.alive()) {
      // Broadphase reaches the largest possible partner; the exact minSep
      // uses the pair's own per-archetype hitboxes.
      const queryRadius = 0.7 * (e.hitbox_radius + MAX_HITBOX_RADIUS);
      sh.forEachInCircle(
        e.pos,
        queryRadius,
        (id) => byId.get(id)?.pos,
        (id) => {
          const n = byId.get(id);
          if (!n || n.pool_index <= e.pool_index) return;
          resolveStaticSeparation(e, n, 0.7 * (e.hitbox_radius + n.hitbox_radius));
        },
        e.id
      );
    }
  }
  // Separation pushes can carry an enemy past the seam; re-wrap so every
  // position stays on the torus.
  for (const e of world.enemies.alive()) {
    wrapArena(e.pos);
  }

  // Player contact damage + knockback (GDD §4.10).
  const enemiesSnapshot = world.enemies.aliveArray();
  // Leech latch bookkeeping: a dash start (iframes set on this tick, the
  // movement.ts dash-start signature) flings every latched leech off; the
  // live latched count gates new latches (def.latch.max_latched).
  const dashIframesTicks = Math.round(cfg.getNumber('player.dash_iframes') * stepRate);
  const dashStarted = player.dash.iframes_until === tick + dashIframesTicks;
  let latchedCount = 0;
  for (const e of enemiesSnapshot) {
    if (isLatched(e, tick)) latchedCount++;
  }
  for (const e of enemiesSnapshot) {
    if (tick < e.telegraph_until) continue;

    // Phantom Vector wake: an enemy crossing the dash path takes the wake
    // damage exactly once per dash (hit sparks/numbers ride the 'hit' event).
    const wake = player.dashWake;
    if (wake && tick < wake.until && !wake.hitIds.includes(e.id)) {
      const abx = wake.bx - wake.ax;
      const aby = wake.by - wake.ay;
      const lenSq = abx * abx + aby * aby;
      let t = lenSq > 1e-12 ? ((e.pos.x - wake.ax) * abx + (e.pos.y - wake.ay) * aby) / lenSq : 0;
      t = Math.max(0, Math.min(1, t));
      const cdx = e.pos.x - (wake.ax + t * abx);
      const cdy = e.pos.y - (wake.ay + t * aby);
      const reach = e.hitbox_radius + 0.5;
      if (cdx * cdx + cdy * cdy <= reach * reach) {
        wake.hitIds.push(e.id);
        e.hp -= wake.damage;
        events.emit(
          'hit',
          {
            target_id: e.id,
            damage: wake.damage,
            crit: false,
            weapon_id: 'dash_wake',
            pos: { x: e.pos.x, y: e.pos.y }
          },
          tick
        );
      }
    }

    // Active latch (EnemyDef.latch): the leech pins to the hull at its
    // per-enemy orbit slot and drains HP through the shield pipeline instead
    // of dealing contact damage. Handled before the overlap test — the pin
    // keeps it inside contact range by construction. Re-pinned here (after
    // the separation pass) so flocking can't shake it loose.
    if (e.latch_until !== undefined) {
      if (isLatched(e, tick)) {
        if (dashStarted) {
          // Dash shake-off: fling radially and start the re-latch cooldown.
          const away = normalize(fastDelta(e.pos, player.pos, vec2()));
          e.vel.x = away.x * LEECH_SHAKEOFF_SPEED;
          e.vel.y = away.y * LEECH_SHAKEOFF_SPEED;
          e.latch_until = undefined;
          e.latch_tick = undefined;
          e.latch_drain_acc = undefined;
          e.latch_detached_tick = tick;
          latchedCount--;
          continue;
        }
        const latchDef = getEnemyDef(e.def_id).latch!;
        const orbitAngle = e.pool_index * 2.1;
        const orbitRadius = player.hitbox_radius * 0.9;
        e.pos.x = player.pos.x + Math.cos(orbitAngle) * orbitRadius;
        e.pos.y = player.pos.y + Math.sin(orbitAngle) * orbitRadius;
        wrapArena(e.pos);
        // Continuous drain (no burst, no mercy window): accumulate dps·dt and
        // apply whole-HP ticks through the standard shield→HP pipeline.
        e.latch_drain_acc = (e.latch_drain_acc ?? 0) + latchDef.dps * dt;
        if (e.latch_drain_acc >= 1) {
          e.latch_drain_acc -= 1;
          applyDamageToPlayer(player, 1, tick);
          events.emit(
            'player_hit',
            {
              enemy_id: e.id,
              damage: 1,
              source: 'leech',
              // Lets render/audio tick the vignette gently for the drain.
              latched: true,
              pos: { x: player.pos.x, y: player.pos.y }
            },
            tick
          );
        }
        continue;
      }
      // Not latched now: the latch either expired naturally (quiet detach —
      // no contact processing this tick, re-latch cooldown starts) or is
      // stale bookkeeping from a previous tenant of this pool slot (cleared
      // silently, no cooldown).
      const expiredNaturally =
        e.latch_tick !== undefined && e.latch_tick >= e.spawn_tick && e.latch_tick <= tick;
      e.latch_until = undefined;
      e.latch_tick = undefined;
      e.latch_drain_acc = undefined;
      if (expiredNaturally) e.latch_detached_tick = tick;
      continue;
    }

    if (!staticCircleOverlap(e.pos, e.hitbox_radius, player.pos, player.hitbox_radius)) continue;

    if (player.dash.iframes_until > tick) {
      // Dash i-frames: zero damage, no knockback.
      continue;
    }
    if (player.mercy_until > tick) {
      // Mercy invincibility window: zero damage.
      continue;
    }

    const def = getEnemyDef(e.def_id);
    // Leech latch (EnemyDef.latch): on contact, latch on instead of dealing a
    // burst hit — unless max_latched leeches already hold on (then it fights
    // like any enemy) or the re-latch cooldown from a recent detach is still
    // running. Same telegraph/i-frame/mercy gates as a burst hit (above), so
    // contact during mercy or dash i-frames never latches. Bosses exempt.
    const latch = e.boss ? undefined : def.latch;
    if (latch && latchedCount < latch.max_latched) {
      const detached = e.latch_detached_tick;
      const cooldownActive =
        detached !== undefined &&
        detached >= e.spawn_tick &&
        detached <= tick &&
        tick - detached < LATCH_RELATCH_COOLDOWN_TICKS;
      if (!cooldownActive) {
        e.latch_until = tick + latch.duration_ticks;
        e.latch_tick = tick;
        e.latch_drain_acc = 0;
        latchedCount++;
        continue;
      }
    }

    const damage = enemyDamageAtTime(def.contact_damage, world.runTime);
    const playerSpeed = len(player.vel);
    let bashed = false;
    if (playerSpeed >= RAM_SPEED_THRESHOLD) {
      // Ram (GDD §4.10 hull concept): the player smashes the enemy for ram
      // damage; the recoil hits the hull before HP instead of the shield.
      // Ram kills fall through to the death/loot pass below.
      const ramDamage = ramDamageForSpeed(playerSpeed);
      e.hp -= ramDamage;
      applyRamDamageToPlayer(player, damage, tick);
      events.emit(
        'hit',
        {
          target_id: e.id,
          damage: ramDamage,
          crit: false,
          weapon_id: 'ram',
          pos: { x: e.pos.x, y: e.pos.y }
        },
        tick
      );
    } else {
      // Slow contact: existing shield/HP pipeline.
      const shieldBefore = player.shield;
      applyDamageToPlayer(player, damage, tick);
      // Shield bash: when the energy shield absorbs (part of) the hit, it
      // flings the attacker off the ship instead of letting it drift
      // through. Bosses are too massive to shove.
      if (!e.boss && shieldBefore > player.shield) {
        bashed = true;
        const away = normalize(fastDelta(e.pos, player.pos, vec2()));
        e.pos.x += away.x * SHIELD_BASH_SHOVE;
        e.pos.y += away.y * SHIELD_BASH_SHOVE;
        wrapArena(e.pos);
        e.vel.x = away.x * SHIELD_BASH_SPEED;
        e.vel.y = away.y * SHIELD_BASH_SPEED;
      }
    }
    player.mercy_until = tick + mercyTicks;

    const pushDir = normalize(fastDelta(player.pos, e.pos, vec2()));
    player.vel.x = pushDir.x * contactKnockback;
    player.vel.y = pushDir.y * contactKnockback;

    events.emit(
      'player_hit',
      {
        enemy_id: e.id,
        damage,
        pos: { x: player.pos.x, y: player.pos.y },
        // Lets render/audio tell a shield bash from a hull/HP hit.
        shield_bash: bashed
      },
      tick
    );
  }

  // Death and loot.
  for (const e of enemiesSnapshot) {
    if (e.hp > 0) continue;
    events.emit(
      'death',
      { entity_id: e.id, enemy_id: e.id, kind: 'enemy', def_id: e.def_id, pos: { x: e.pos.x, y: e.pos.y } },
      tick
    );
    spawnXpMote(world, e.pos, getEnemyDef(e.def_id).drops.xp_motes, tick);
    world.enemies.release(e);
  }
}
